import {Form, List, Popup} from "semantic-ui-react"
import {pastPassengerToPassenger} from "../model/passenger/passenger"
import {PassengerFormData, PastPassenger} from "../model/passenger/types"
import {PassengerForm} from "./PassengerForm"
import {Passenger as PassengerComponent} from "./Passenger"
import {arrayDelete, arraySet, assertNumber} from "../tsutil"
import * as React from "react"
import {fromNullable} from "fp-ts/lib/Option"
import {BookingFormPassengerState} from "../state"
import {MapState} from "./BookingDetails"
import {PastPassengerFragment} from "../graphql/queries"

export function needPeopleMover(passengerCount: number) {
  return passengerCount > 4 && passengerCount < 8
}

function pastPassengerOptions(pastPassengers: Array<PastPassenger>) {
  return pastPassengers.map((p: PastPassenger, index: number) => ({
    // This synthetic key matches the GROUP BY in the database view
    key: `${p.name}:${p.email}:${p.mobile}:${p.phone}`,
    text: `(${p.count}) ${p.name}`,
    value: index,
  }))
}

export interface PassengerFieldsState {
  initialPassenger: PassengerFormData
  passengers: Array<BookingFormPassengerState>
  editingPassengerIndex: number | null
  editingPassenger: boolean
}

interface Props {
  state: PassengerFieldsState
  mapState: MapState<PassengerFieldsState> | null
  disabled: boolean
  isAdmin: boolean
  forBookerId: string | null
  pastPassengers: Array<PastPassenger>
}

export function PassengerFields(props: Props) {
  function render() {
    const {state, disabled, mapState, isAdmin, forBookerId, pastPassengers} = props
    const {
      editingPassengerIndex: index,
      editingPassenger: isEditingPassenger,
      passengers,
      initialPassenger,
    } = state

    return (
      <>
        {passengers.length > 0 && (
          <Form.Field style={{marginTop: "1em"}}>
            <label>Primary passenger</label>
            <PassengerComponent
              onEdit={makeOnEdit(0)}
              onDelete={makeOnDelete(0)}
              passenger={passengers[0]}
              disabled={disabled || isEditingPassenger}
            />
          </Form.Field>
        )}
        {passengers.length > 1 && (
          <Form.Field>
            <label>Other passengers</label>
            <List
              items={passengers.slice(1).map((pass, index) => (
                <PassengerComponent
                  onEdit={makeOnEdit(index + 1)}
                  onDelete={makeOnDelete(index + 1)}
                  key={index /* PassengerComponent is stateless so index as key is fine */}
                  passenger={pass}
                  disabled={disabled || isEditingPassenger}
                />
              ))}
            />
          </Form.Field>
        )}
        {mapState &&
          (isEditingPassenger ? (
            <>
              {index === null && (
                <PastPassengersField
                  forBookerId={forBookerId}
                  disabled={false}
                  pastPassengers={pastPassengers}
                  onPastPassenger={(pp) => {
                    mapState((state) => {
                      return {
                        ...state,
                        passengers: [...state.passengers, pastPassengerToPassenger(pp)],
                        editingPassenger: false,
                      }
                    })
                  }}
                />
              )}
              <PassengerForm
                passenger={index !== null ? passengers[index] : initialPassenger}
                onReset={() => {
                  mapState((state) => ({
                    ...state,
                    editingPassenger: false,
                  }))
                }}
                onSave={(passenger) => {
                  mapState((state) => {
                    const passengers =
                      index !== null
                        ? arraySet(state.passengers, index, passenger)
                        : [...state.passengers, passenger]
                    return {
                      ...state,
                      editingPassenger: false,
                      passengers,
                    }
                  })
                }}
                // Mobile is required if the passenger being modified is the primary passenger,
                // unless the person is admin
                mobileRequired={fromNullable(index).getOrElse(passengers.length) === 0 && !isAdmin}
              />
            </>
          ) : (
            <Form.Button
              disabled={disabled}
              icon="add"
              labelPosition="left"
              content="Add passenger"
              onClick={(e) => {
                e.preventDefault()
                mapState((state) => ({
                  ...state,
                  editingPassenger: true,
                  editingPassengerIndex: null,
                }))
              }}
            />
          ))}
      </>
    )
  }

  function makeOnEdit(index: number): (() => void) | null {
    const {mapState, disabled} = props
    return !mapState
      ? null
      : () => {
          if (disabled) {
            return
          }
          mapState((state) => ({
            ...state,
            editingPassenger: true,
            editingPassengerIndex: index,
          }))
        }
  }

  function makeOnDelete(index: number): (() => void) | null {
    const {mapState, disabled} = props
    return !mapState
      ? null
      : () => {
          if (disabled) {
            return
          }
          mapState((state) => {
            const passengers = arrayDelete(state.passengers, index)
            return {
              ...state,
              // Open the form if there are no passengers left
              editingPassengerIndex: passengers.length === 0 ? null : state.editingPassengerIndex,
              editingPassenger: passengers.length === 0 || state.editingPassenger,
              passengers,
            }
          })
        }
  }

  return render()
}

interface PastPassengersProps {
  forBookerId: string | null
  disabled: boolean
  onPastPassenger: (pp: PastPassengerFragment) => void
  pastPassengers: Array<PastPassenger>
}

function PastPassengersField(props: PastPassengersProps) {
  const {disabled, onPastPassenger, pastPassengers} = props
  if (pastPassengers.length === 0) {
    return null
  }
  return (
    <Form.Field width={10}>
      <Popup
        trigger={
          <Form.Dropdown
            name="pastPassenger"
            selection
            placeholder="Quick passenger"
            search={true}
            onChange={(e, field) => {
              onPastPassenger(pastPassengers[assertNumber(field.value)])
            }}
            selectOnNavigation={false}
            selectOnBlur={false}
            fluid
            value=""
            options={pastPassengerOptions(pastPassengers)}
            disabled={disabled}
          />
        }
        content="Quickly select a passenger you entered on a previous booking."
      />
    </Form.Field>
  )
}



// WEBPACK FOOTER //
// ./src/components/PassengerFields.tsx